import React, { useContext, useEffect, useState } from "react"
import PropTypes from "prop-types"
import FormWrapper from "components/FormWrapper"
import { Input } from "components/Inputs"
import TextEditor from "components/TextEditor"
import useMessaging from "hooks/fetch/useMessaging"
import { EditorState, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import { useSelector } from "react-redux"
import dayjs from 'dayjs'
import useDebounce from "hooks/useDebounce"
import useProviders from "hooks/fetch/useProviders"
import Suggestion from "../Suggestion"
import { MessagingContext } from "context"
import useUpload from 'hooks/useUpload'

export default function NewMessage() {
    const { currentTab, setCreateMessage } = useContext(MessagingContext)
    const { upload, uploading } = useUpload()
    const { loggedUser } = useSelector(state => state.auth)
    const { get_providers, loading: loadingProviders } = useProviders()
    const [recipientSuggesstions, setRecipientSuggestions] = useState([])
    const [CcSuggesstions, setCcSuggestions] = useState([])
    const [isTyping, setIsTyping] = useState(false)
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty())
    const { create_conversation, get_conversations, loading } = useMessaging()
    const [file, setFile] = useState()
    const [formData, setFormData] = useState({
        subject: '',
        patient: loggedUser?._id,
        provider: '',
        cc: '',
        recipient: '',
        ccProviders: []
    })
    const message = draftToHtml(convertToRaw(editorState.getCurrentContent()))

    const debouncedRecipient = useDebounce(formData.recipient, 1000)
    const debouncedCc = useDebounce(formData.cc, 1000)

    const handleChange = (e) => {
        setIsTyping(true)
        setFormData({ ...formData, [e.target.name]: e.target.value })
    }

    const removeCc = (id) => {
        setFormData({
            ...formData,
            ccProviders: formData.ccProviders.filter((item) => item?.id !== id)
        })
    }

    const handleEditorState = (e) => {
        setEditorState(e)
    }

    const handleFile = (e) => {
        setFile(e.target.files[0])
    }

    const selectRecipient = (recipient) => {
        setIsTyping(false)
        setFormData({
            ...formData, provider: recipient?._id,
            recipient: `${recipient?.firstName} ${recipient?.lastName}`
        })
        setRecipientSuggestions([])
    }

    const selectCc = (recipient) => {
        setIsTyping(false)
        setFormData({
            ...formData,
            ccProviders: [
                ...formData.ccProviders,
                {
                    id: recipient?._id,
                    name: (recipient?.qualification ? `${recipient?.qualification}. ` : '') + `${recipient?.firstName} ${recipient?.lastName}`
                }
            ],
            cc: ''
        })
        setCcSuggestions([])
    }

    useEffect(() => {
        if (debouncedCc && isTyping) {
            setRecipientSuggestions([])
            get_providers({ search: debouncedCc })
                .then((res) => setCcSuggestions(res?.data?.providers || []))
            return
        }
        if (debouncedRecipient && isTyping) {
            setCcSuggestions([])
            get_providers({ search: debouncedRecipient })
                .then((res) => setRecipientSuggestions(res?.data?.providers || []))
        }
    }, [debouncedCc, debouncedRecipient])

    const submit = () => {
        const messageTab = currentTab === 'Trash' ? 'trash' : 'messages'
        if (file) {
            let timestamp = dayjs()?.format('YYYYMMDD-HHmmss');
            upload(file, { file: `${timestamp}${file?.name}` }, timestamp, false)
                .then(res => {
                    if (res?.response?.status === 200) {
                        create_conversation({ ...formData, message, file: res?.imageUrl, fileType: file?.type.split("/")[1] })
                            .then(res => {
                                if (res?.code === 200) {
                                    setCreateMessage(false)
                                    get_conversations({ type: messageTab })
                                }
                            })
                    }
                })
        } else {
            create_conversation({ ...formData, message })
                .then(res => {
                    if (res?.code === 200) {
                        setCreateMessage(false)
                        get_conversations({ type: messageTab })
                    }
                })
        }
    }

    return (
        <FormWrapper className="w-full" onSubmit={submit}>
            <div>
                <div className="mb-[24px]">
                    <Input
                        label="Subject"
                        type="text"
                        id="subject"
                        name="subject"
                        readOnly={loading}
                        value={formData?.subject}
                        onChange={handleChange}
                    />
                </div>
                <div className="mb-[24px]">
                    <Input
                        label="Recipient"
                        type="text"
                        id="recipient"
                        name="recipient"
                        readOnly={loading}
                        value={formData?.recipient}
                        onChange={handleChange}
                    />
                    {recipientSuggesstions?.length ?
                        <Suggestion
                            suggestions={recipientSuggesstions}
                            onSelect={selectRecipient}
                            loading={loadingProviders}
                        /> : ''
                    }
                </div>
                <div>
                    <Input
                        label="Cc"
                        type="text"
                        id="cc"
                        name="cc"
                        readOnly={loading}
                        value={formData.cc}
                        onChange={handleChange}
                    />
                    {CcSuggesstions?.length ?
                        <Suggestion
                            suggestions={CcSuggesstions}
                            onSelect={selectCc}
                            loading={loadingProviders}
                        /> : ''
                    }
                    <ul className="flex items-center mt-[8px] mb-[43px]">
                        {formData?.ccProviders?.map((item) =>
                            <li
                                key={item?.id}
                                className="flex items-center text-14 font-campton_r text-neutral_black mr-[8px] rounded-[100px] border border-neutral_stroke_2 px-[12px] py-[5.5px]"
                            >
                                <p className="mr-[9.5px]">{item?.name}</p>
                                <div
                                    data-testid={item?.id}
                                    onClick={() => removeCc(item?.id)}
                                >
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer hide_tap">
                                        <path d="M9.35375 5.35375L7.70688 7L9.35375 8.64625C9.40021 8.69271 9.43706 8.74786 9.4622 8.80855C9.48734 8.86925 9.50028 8.9343 9.50028 9C9.50028 9.0657 9.48734 9.13075 9.4622 9.19145C9.43706 9.25214 9.40021 9.30729 9.35375 9.35375C9.3073 9.40021 9.25215 9.43705 9.19145 9.4622C9.13075 9.48734 9.0657 9.50028 9 9.50028C8.93431 9.50028 8.86925 9.48734 8.80855 9.4622C8.74786 9.43705 8.69271 9.40021 8.64625 9.35375L7 7.70687L5.35375 9.35375C5.3073 9.40021 5.25215 9.43705 5.19145 9.4622C5.13075 9.48734 5.0657 9.50028 5 9.50028C4.93431 9.50028 4.86925 9.48734 4.80855 9.4622C4.74786 9.43705 4.69271 9.40021 4.64625 9.35375C4.5998 9.30729 4.56295 9.25214 4.53781 9.19145C4.51266 9.13075 4.49972 9.0657 4.49972 9C4.49972 8.9343 4.51266 8.86925 4.53781 8.80855C4.56295 8.74786 4.5998 8.69271 4.64625 8.64625L6.29313 7L4.64625 5.35375C4.55243 5.25993 4.49972 5.13268 4.49972 5C4.49972 4.86732 4.55243 4.74007 4.64625 4.64625C4.74007 4.55243 4.86732 4.49972 5 4.49972C5.13268 4.49972 5.25993 4.55243 5.35375 4.64625L7 6.29313L8.64625 4.64625C8.69271 4.59979 8.74786 4.56294 8.80855 4.5378C8.86925 4.51266 8.93431 4.49972 9 4.49972C9.0657 4.49972 9.13075 4.51266 9.19145 4.5378C9.25215 4.56294 9.3073 4.59979 9.35375 4.64625C9.40021 4.6927 9.43706 4.74786 9.4622 4.80855C9.48734 4.86925 9.50028 4.9343 9.50028 5C9.50028 5.0657 9.48734 5.13075 9.4622 5.19145C9.43706 5.25214 9.40021 5.3073 9.35375 5.35375ZM13.5 7C13.5 8.28558 13.1188 9.54229 12.4046 10.6112C11.6903 11.6801 10.6752 12.5132 9.48744 13.0052C8.29973 13.4972 6.99279 13.6259 5.73192 13.3751C4.47104 13.1243 3.31285 12.5052 2.40381 11.5962C1.49477 10.6872 0.875703 9.52896 0.624899 8.26809C0.374095 7.00721 0.502816 5.70028 0.994786 4.51256C1.48676 3.32484 2.31988 2.30968 3.3888 1.59545C4.45772 0.881218 5.71442 0.5 7 0.5C8.72335 0.50182 10.3756 1.18722 11.5942 2.40582C12.8128 3.62441 13.4982 5.27665 13.5 7ZM12.5 7C12.5 5.9122 12.1774 4.84883 11.5731 3.94436C10.9687 3.03989 10.1098 2.33494 9.10476 1.91866C8.09977 1.50238 6.9939 1.39346 5.92701 1.60568C4.86011 1.8179 3.8801 2.34172 3.11092 3.11091C2.34173 3.8801 1.8179 4.86011 1.60568 5.927C1.39347 6.9939 1.50238 8.09977 1.91867 9.10476C2.33495 10.1098 3.0399 10.9687 3.94437 11.5731C4.84884 12.1774 5.91221 12.5 7 12.5C8.45819 12.4983 9.85617 11.9184 10.8873 10.8873C11.9184 9.85617 12.4983 8.45818 12.5 7Z" fill="#748181" />
                                    </svg>
                                </div>
                            </li>
                        )}
                    </ul>
                    <div className="editor_shadow mx-[-24px] px-[24px] pt-[16px]">
                        <TextEditor
                            editorState={editorState}
                            handleEditorStateChange={handleEditorState}
                            file={file}
                            disabled={loading || !formData?.subject || !formData?.provider || uploading || (!message.split('<p></p>')[0] && !file)}
                            loading={loading || uploading}
                            handleFile={handleFile}
                        />
                    </div>
                </div>
            </div>
        </FormWrapper>
    )
}
